import React from 'react';
import { NavigateFunction } from 'react-router';
import { z } from 'zod';

import styles from '../styles.module.css';
import {
  LocationData,
  LocationApiResponse,
  LocationAccessLevelsApiResponse
} from './types';
import StatusCircleIcon from '@/assets/icons/status-circle.svg?react';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import { DataTableColumn } from '@/components/DataTable';
import { adminToolPaths } from '@/constants/adminTool';
import { LocationFormData } from '@/hooks/services/Locations';
import {
  apiDateToFormDateValue,
  getUTC
} from '@/solaris-lens-common-lib/src/utils/dates';
import { TurbineApiData } from '@/types/turbines';

const StatusIndicator = ({ location }: { location: LocationData }) => {
  const getStatus = (location: LocationData) => {
    if (location.status === 'Active') {
      return { text: 'Active', color: 'var(--color-green)' };
    } else {
      return { text: 'Inactive', color: 'var(--color-orange)' };
    }
  };

  const status = getStatus(location);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{status?.text}</span>
      <div
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: status?.color
        }}
      />
    </div>
  );
};

export const locationSchema = z.object({
  name: z.string().min(1, 'The name is required'),
  customer: z.string().min(1, 'The customer is required')
});

export const parseLocationSchema = (parsedData: any) => {
  const errors: { [k: string]: string } = {};
  parsedData.error.errors.forEach(
    (e: { path: { toString: () => string | number }[]; message: string }) => {
      if (e.path[0]) errors[e.path[0].toString()] = e.message;
    }
  );
  return errors;
};

export type ValidationErrors = {
  [K in keyof LocationFormData]?: string;
};

export const validateField = (
  field: keyof LocationFormData,
  value: any,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
) => {
  try {
    const fieldSchema =
      locationSchema.shape[field as keyof typeof locationSchema.shape];
    if (fieldSchema) {
      fieldSchema.parse(value);
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error.errors[0]?.message
      }));
    }
  }
};

export const validateForm = (
  formData: LocationFormData,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
  setShowValidationErrors: React.Dispatch<React.SetStateAction<boolean>>
): boolean => {
  try {
    locationSchema.parse(formData);
    setValidationErrors({});
    setShowValidationErrors(false);
    return true;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: ValidationErrors = {};
      error.errors.forEach(err => {
        if (err.path.length > 0) {
          const fieldPath = err.path.join('.') as keyof LocationFormData;
          errors[fieldPath] = err.message;
        }
      });
      setValidationErrors(errors);
      setShowValidationErrors(true);
      return false;
    }
    return false;
  }
};

export const createHandleInputChange = (
  setFormData: React.Dispatch<React.SetStateAction<LocationFormData>>,
  showValidationErrors: boolean,
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>
) => {
  return (field: keyof LocationFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (showValidationErrors) {
      validateField(field, value, setValidationErrors);
    }
  };
};

export const filterLocations = (
  locations: LocationData[],
  searchTerm: string,
  showActive: boolean,
  showInactive: boolean
): LocationData[] => {
  return locations.filter((location: LocationData) => {
    try {
      const turbineCount = location.turbines ? location.turbines.length : 0;

      const matchesSearch =
        searchTerm === '' ||
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        turbineCount.toString().includes(searchTerm);

      const isLocationActive = location.status === 'Active';
      const isLocationInactive = !isLocationActive;
      const matchesStatusFilter =
        (showActive && isLocationActive) ||
        (showInactive && isLocationInactive);

      return matchesSearch && matchesStatusFilter;
    } catch (error) {
      console.error('Error filtering location:', error);
      return false;
    }
  });
};

export const createLocationsColumns = (
  navigate: NavigateFunction
): DataTableColumn<LocationData>[] => [
  {
    field: 'name',
    headerName: 'NAME',
    width: 'auto',
    sortable: true,
    onClick: row => {
      navigate(adminToolPaths.locations.detail.replace(':locationId', row.id));
    }
  },
  {
    field: 'turbines',
    headerName: '# OF TURBINES',
    width: 'auto',
    sortable: true,
    type: 'number',
    renderCell: ({ row }) => (row.turbines ? row.turbines.length : 0)
  },
  {
    field: 'status',
    headerName: 'STATUS',
    width: 'auto',
    sortable: true,
    renderCell: ({ row }) => <StatusIndicator location={row} />
  }
];

export const MODAL_DELAYS = {
  CLOSE_DELAY: 300,
  SUCCESS_AUTO_CLOSE: 3000,
  ERROR_AUTO_CLOSE: 5000
} as const;

export interface ModalContentConfig {
  modalTitle?: string;
  modalButtons?: any[];
  autoClose?: number;
}

export interface SuccessModalData {
  title: string;
  message?: string;
  icon?: React.ComponentType;
}

export interface ErrorModalData {
  title: string;
  message: string;
  icon?: React.ComponentType;
}

export const createSuccessModalContent = (data: SuccessModalData) => (
  <div className={styles.successModalContainer}>
    <div className={styles.successModalIcon}>
      {data.icon ? <data.icon /> : <SuccessIcon />}
    </div>
    <h2 className={styles.successModalTitle}>{data.title}</h2>
    {data.message && data.message}
  </div>
);

export const createErrorModalContent = (data: ErrorModalData) => (
  <div className={styles.successModalContainer}>
    <div className={styles.successModalIcon}>
      {data.icon ? <data.icon /> : <StatusCircleIcon />}
    </div>
    <h2 className={styles.errorModalTitle}>{data.title}</h2>
    <p className={styles.errorModalMessage}>{data.message}</p>
  </div>
);

export const getSuccessModalConfig = (
  autoClose: number = MODAL_DELAYS.SUCCESS_AUTO_CLOSE
): ModalContentConfig => ({
  modalTitle: '',
  modalButtons: [],
  autoClose
});

export const getErrorModalConfig = (
  autoClose: number = MODAL_DELAYS.ERROR_AUTO_CLOSE
): ModalContentConfig => ({
  modalTitle: '',
  modalButtons: [],
  autoClose
});

export const showSuccessModal = (
  updateModalContent: (
    content: React.ReactNode,
    config: ModalContentConfig
  ) => void,
  data: SuccessModalData,
  onComplete?: () => void,
  delay: number = MODAL_DELAYS.CLOSE_DELAY
) => {
  setTimeout(() => {
    updateModalContent(
      createSuccessModalContent(data),
      getSuccessModalConfig()
    );

    if (onComplete) {
      setTimeout(onComplete, MODAL_DELAYS.SUCCESS_AUTO_CLOSE);
    }
  }, delay);
};

export const showErrorModal = (
  updateModalContent: (
    content: React.ReactNode,
    config: ModalContentConfig
  ) => void,
  data: ErrorModalData,
  delay: number = MODAL_DELAYS.CLOSE_DELAY
) => {
  setTimeout(() => {
    updateModalContent(createErrorModalContent(data), getErrorModalConfig());
  }, delay);
};

export const handleApiError = (
  error: unknown,
  defaultMessage: string
): string => {
  return error instanceof Error ? error.message : defaultMessage;
};

export const createTurbineUpdateData = (
  turbines: TurbineApiData[]
): { id: string }[] => {
  return turbines.map(turbine => ({ id: turbine.id }));
};

export const combineTurbines = (
  existingTurbines: TurbineApiData[],
  newTurbines: TurbineApiData[]
): { id: string }[] => {
  const existing = createTurbineUpdateData(existingTurbines);
  const newOnes = createTurbineUpdateData(newTurbines);
  return [...existing, ...newOnes];
};

export const filterOutTurbine = (
  turbines: TurbineApiData[],
  turbineToRemove: TurbineApiData
): { id: string }[] => {
  return turbines
    .filter(turbine => turbine.id !== turbineToRemove.id)
    .map(turbine => ({ id: turbine.id }));
};

export const createLocationUpdatePayload = (
  name: string,
  category: string,
  turbines: { id: string }[],
  dataPointVisibility: string[],
  additionalData?: {
    address?: string;
    city?: string;
    state?: string;
    latitude?: string;
    longitude?: string;
    customer?: string;
    startDate?: string;
    status?: string;
  }
) => {
  const { startDate: startDateRaw, ...restAdditional } = additionalData || {};
  const startDateForApi =
    startDateRaw && startDateRaw.length > 0
      ? getUTC(startDateRaw).dateUTC
      : '';

  return {
    name,
    category,
    turbines,
    status: additionalData?.status || 'Active',
    geoReference: {
      latitude: additionalData?.latitude || '',
      longitude: additionalData?.longitude || ''
    },
    customer: additionalData?.customer || '',
    startDate: startDateForApi,
    dataPointVisibility: dataPointVisibility,
    ...restAdditional
  };
};

export const transformTurbinesForModal = (
  turbines: TurbineApiData[] | undefined,
  isError: boolean = false
): TurbineApiData[] => {
  if (isError || !turbines || !Array.isArray(turbines)) return [];

  return turbines.map(turbine => {
    const turbineWithIsActive = turbine as TurbineApiData & {
      isActive?: boolean;
    };
    if (
      'isActive' in turbineWithIsActive &&
      turbineWithIsActive.isActive !== undefined
    ) {
      return {
        ...turbine,
        isActive: Boolean(turbineWithIsActive.isActive)
      };
    }
    return turbine;
  });
};

export const filterUnassignedTurbines = (
  turbines: TurbineApiData[] | undefined,
  searchTerm: string
): TurbineApiData[] => {
  if (!turbines || !Array.isArray(turbines)) return [];

  if (!searchTerm) return turbines;

  return turbines.filter(
    turbine =>
      turbine?.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      turbine?.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      turbine?.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

export const transformCompaniesForModal = (
  companies: any[],
  isError: boolean = false
): any[] => {
  if (isError) return [];

  return (companies || []).map(company => ({
    id: String(company.id),
    name: company.name,
    turbines: [],
    status:
      company.isActive === '1' ? ('Active' as const) : ('Inactive' as const)
  }));
};

export const transformUsersForModal = (
  users: any[],
  isError: boolean = false
): any[] => {
  if (isError) return [];

  return (users || []).map(user => {
    const username = user.username || user.id || '';
    const name = user.attributes?.name || user.name || '';

    return {
      id: username,
      name: name || username,
      username: username,
      lastName: user.attributes?.lastName || user.lastName || '',
      email: user.attributes?.email || user.email || '',
      userRole:
        (user.attributes?.groups?.[0] as
          | 'Admin'
          | 'Operator'
          | 'Viewer'
          | 'Manager') ||
        user.userRole ||
        'Viewer'
    };
  });
};

export const filterLocalCompanies = (
  companies: LocationAccessLevelsApiResponse['data']['companies'],
  searchTerm: string
): any[] => {
  const baseTransform = (
    company: LocationAccessLevelsApiResponse['data']['companies'][0]
  ) => ({
    id: company.id || '',
    name: company.name || '',
    turbines: company.turbines || [],
    status: company.status || 'Active'
  });

  if (!searchTerm) {
    return companies.map(baseTransform);
  }

  return companies
    .filter(company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map(baseTransform);
};

export const filterLocalUsers = (
  users: LocationAccessLevelsApiResponse['data']['users'],
  searchTerm?: string
): any[] => {
  const baseTransform = (
    user: LocationAccessLevelsApiResponse['data']['users'][0]
  ) => ({
    id: user.id || user.username || '',
    name: user.name || user.username || user.id || 'N/A',
    lastName: user.lastName || 'N/A',
    email: user.email || user.attributes?.email || 'N/A',
    userRole:
      user.userRole ||
      user.attributes?.groups?.[0] ||
      user.groups?.[0] ||
      'Viewer'
  });

  if (!searchTerm) {
    return users.map(baseTransform);
  }

  return users
    .filter(
      user =>
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.lastName || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map(baseTransform);
};

export const getPluralizedText = (
  count: number,
  singular: string,
  plural: string
) => ({
  text: count > 1 ? plural : singular,
  verb: count > 1 ? 'were' : 'was'
});

export const updateGeneralLocationData = (
  locationData: LocationApiResponse['data'],
  turbinesCount: number
) => {
  const dataPointVisibility = locationData.dataPointVisibility || [];
  const dataPoints: Record<string, boolean> = {};
  dataPointVisibility.forEach((key: string) => {
    dataPoints[key] = true;
  });

  return {
    id: locationData.id || '',
    name: locationData.name || '',
    numberOfTurbines: turbinesCount.toString(),
    address: locationData.address || '',
    city: locationData.city || '',
    nearestCity: locationData.nearestCity || '',
    state: locationData.state || '',
    latitude: locationData.geoReference?.latitude || '',
    longitude: locationData.geoReference?.longitude || '',
    customer: locationData.customer || '',
    startDate: apiDateToFormDateValue(locationData.startDate),
    status: (locationData.status as 'Active' | 'Inactive') || 'Active',
    dataPoints
  };
};

export const createDebounceEffect = (
  value: string,
  setValue: React.Dispatch<React.SetStateAction<string>>,
  delay: number = 500
) => {
  return () => {
    const timer = setTimeout(() => {
      setValue(value);
    }, delay);

    return () => clearTimeout(timer);
  };
};
